import { useState } from 'react'

import {
  Box,
  Button,
  Card,
  Divider,
  FormRow,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import { FileItem } from '@factorialco/f0-react/dist/experimental'
import { SpaceControlWorkSpaceWorkSpaceTypeEnumValues } from 'generated/resources/datatable.graphql'
import {
  CreateWorkSpaceMutationVariables,
  GetWorkSpacesDocument,
  UpdateWorkSpaceMutationVariables,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Select'
import Sidepanel from 'modules/core/components/Sidepanel'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import TimePicker from 'modules/core/components/TimePicker'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import FileUploader from 'modules/finance/components/FileUploader'
import { useCreateWorkSpace } from 'modules/space_control/hooks/useCreateWorkSpace'
import { useUpdateWorkSpace } from 'modules/space_control/hooks/useUpdateWorkSpace'
import type { WorkSpace } from 'modules/space_control/hooks/useWorkSpace'
import {
  workSpaceTypeBackLabel,
  workSpaceTypesDic,
} from 'modules/space_control/store/workSpaceTypes'

interface Props {
  onClose: () => void
  workSpace?: WorkSpace | null
  workAreaId: string
  locationId: string
  picturesEnabled?: boolean
}
interface WorkSpaceFormValues {
  id: string
  name: string
  workSpaceType: string
  capacity: number
  description: string
  startTime: string
  endTime: string
  slotSetup: string
  slotDuration: string
  allowMultipleBookings: boolean
  workAreaId: string
  locationId: string
  images: Array<File>
}

const AddWorkSpace = ({ workSpace, onClose, workAreaId, locationId }: Props) => {
  const { createWorkSpace } = useCreateWorkSpace()
  const { updateWorkSpace } = useUpdateWorkSpace()
  const [selectedFiles, setSelectedFiles] = useState<Array<File>>([])
  const [imagesIdsToRemove, setImagesIdsToRemove] = useState<string[]>([])

  const timeSlotPreviouslySelected = workSpace?.slotSetup
    ? workSpace?.slotSetup === 'minutes'
    : false
  const [isTimeSlot, setIsTimeSlot] = useState(timeSlotPreviouslySelected)

  const slotConfigurationOptions = [
    {
      label: i18n.t('settings.locations.location.work_spaces.add.slot_config.whole_day'),
      value: 'whole_day',
    },
    {
      label: i18n.t('settings.locations.location.work_spaces.add.slot_config.half_day'),
      value: 'half_day',
    },
    {
      label: i18n.t('settings.locations.location.work_spaces.add.slot_config.time_slots'),
      value: 'minutes',
    },
  ]

  const convertToHoursMinutes = (slot_duration: number) => {
    return moment.utc(slot_duration * 60 * 1000).format('HH:mm')
  }

  const closeSidePanel = () => {
    track(trackingEvents.spaceControl.cancelSpace, {})

    onClose()
  }

  const handleUpdate = async (variables: UpdateWorkSpaceMutationVariables) => {
    if (!variables.workAreaId) {
      throw new Error('WorkSpace must have valid workArea and location')
    }
    const result = await updateWorkSpace(variables, {
      refetchQueries: [],
    })
    const handled = handleResult(result, (errors) => {
      workSpaceForm.setErrors({ ...errors })
    })

    return handled
  }

  const handleCreate = async (variables: CreateWorkSpaceMutationVariables) => {
    const result = await createWorkSpace(variables, {
      refetchQueries: [
        {
          query: GetWorkSpacesDocument,
          variables: { locationIds: [locationId], workAreaIds: [workAreaId] },
        },
      ],
    })
    const handled = handleResult(result, (errors) => {
      workSpaceForm.setErrors({ ...errors })
    })

    return handled
  }

  const defaultAllowMultipleBookingsValue = ({
    workSpaceType,
    multipleBookingsValue,
  }: {
    workSpaceType?: string | null
    multipleBookingsValue?: boolean
  }) => {
    if (
      workSpaceType === workSpaceTypesDic.meetingroom ||
      workSpaceType === workSpaceTypesDic.booth
    ) {
      return false
    } else if (workSpaceType === workSpaceTypesDic.other) {
      return multipleBookingsValue ?? true
    } else {
      return true
    }
  }

  const workSpaceForm = useForm<WorkSpaceFormValues>({
    enableReinitialize: true,
    defaultValues: {
      id: workSpace?.id ?? '0',
      name: workSpace?.name || '',
      description: workSpace?.description || '',
      workAreaId: workAreaId,
      locationId: locationId,
      startTime: workSpace?.startTime || '00:00',
      endTime: workSpace?.endTime || '23:59',
      slotSetup: workSpace?.slotSetup ? workSpace.slotSetup : 'whole_day',
      workSpaceType:
        workSpace?.workSpaceTypeValue ?? SpaceControlWorkSpaceWorkSpaceTypeEnumValues?.[0] ?? '',
      allowMultipleBookings: defaultAllowMultipleBookingsValue({
        workSpaceType: workSpace?.workSpaceTypeValue,
        multipleBookingsValue: workSpace?.allowMultipleBookings ?? false,
      }),
      capacity: workSpace?.capacity || 0,
      slotDuration: convertToHoursMinutes(Number(workSpace?.slotDuration) || 0),
      images: [],
    },
    validate: (data) => {
      const errors: FormErrors<WorkSpaceFormValues> = {}

      if (data.slotSetup === 'minutes' && data.slotDuration == '00:00') {
        errors.slotDuration = i18n.t(
          'settings.locations.location.work_spaces.add.greater_than_zero'
        )
      }
      if (data.capacity < 1)
        errors.capacity = i18n.t('settings.locations.location.work_spaces.add.greater_than_zero')

      if (data.endTime <= data.startTime) {
        errors.endTime = i18n.t(
          'settings.locations.location.work_spaces.add.endtime_greater_than_starttime'
        )
      }
      return errors
    },
    onSubmit: async (data) => {
      let slotDuration
      if (data.slotSetup === 'minutes') {
        slotDuration = data.slotDuration
      } else {
        const startTime = moment(data.startTime, 'HH:mm')
        const endTime = moment(data.endTime, 'HH:mm')
        const duration = moment.duration(endTime.diff(startTime, 'minutes'))
        slotDuration = moment.utc(duration.asMinutes()).format('HH:mm')
      }

      const values = {
        ...data,
        allowMultipleBookings: defaultAllowMultipleBookingsValue({
          workSpaceType: data.workSpaceType,
          multipleBookingsValue: data.allowMultipleBookings,
        }),
        workAreaId: workAreaId,
        slotDuration: slotDuration,
        images: selectedFiles,
        imagesIdsToRemove: workSpace ? imagesIdsToRemove : [],
      }

      const handled = workSpace ? await handleUpdate(values) : await handleCreate(values)

      if (handled.success) {
        const valuesToTrack = { ...values, images: selectedFiles.map((file) => file.name) }
        track(trackingEvents.spaceControl.publishSpace, valuesToTrack)
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        onClose()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('settings.locations.location.work_spaces.save_space_error_message'),
        })
      }

      return handled
    },
  })

  const isSubmitActive = Object.keys(workSpaceForm.errors).length === 0

  const onChangeTimeSlot = (value: string) => {
    workSpaceForm.setFieldValue('slotSetup', value)
    if (value === 'minutes') {
      setIsTimeSlot(true)
    } else {
      setIsTimeSlot(false)
    }
  }

  const modalTitle = workSpace
    ? i18n.t('settings.locations.location.work_spaces.edit.title')
    : i18n.t('settings.locations.location.work_spaces.add.title')

  const modalButton = workSpace
    ? i18n.t('settings.locations.location.work_spaces.edit.button')
    : i18n.t('settings.locations.location.work_spaces.add.button')

  const typeSpaceOptions = SpaceControlWorkSpaceWorkSpaceTypeEnumValues.map((type) => {
    return { value: type, label: workSpaceTypeBackLabel(type) }
  })

  const multipleBookingsSwithIsEnabled = () => {
    if (workSpaceForm.values.workSpaceType === workSpaceTypesDic.other) {
      return true
    } else {
      return false
    }
  }

  return (
    <Sidepanel
      onClose={closeSidePanel}
      closeLabel="close"
      title={modalTitle}
      open
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('settings.locations.location.work_spaces.add.cancel')}
          </Button>
          <Button onClick={() => workSpaceForm.handleSubmit()} disabled={!isSubmitActive}>
            {modalButton}
          </Button>
        </>
      }
    >
      <FormRow padding="s0">
        <Box gap="s24">
          <Text.Normal weight="medium" color="grey1000">
            {i18n.t('settings.locations.location.work_spaces.add.subtitle_basics')}
          </Text.Normal>
          <TextField
            label={i18n.t('settings.locations.location.work_spaces.add.name')}
            value={workSpaceForm.values.name}
            onChange={(value) => workSpaceForm.setFieldValue('name', value)}
            mandatory
            attachmentPosition="bottom left"
          />
          <Box flexDirection="row" gap="s16">
            <Select
              label={i18n.t('settings.locations.location.work_spaces.add.type')}
              options={typeSpaceOptions}
              value={workSpaceForm.values.workSpaceType}
              onChange={(value) => {
                workSpaceForm.setFieldValue('workSpaceType', value)
              }}
            />
            <TextField
              label={i18n.t('settings.locations.location.work_spaces.add.capacity')}
              value={workSpaceForm.values.capacity.toString()}
              onChange={(value) => {
                workSpaceForm.setFieldValue('capacity', !isNaN(+value) && +value)
              }}
              mandatory
              error={workSpaceForm.errors.capacity}
              attachmentPosition="bottom left"
            />
          </Box>
          <TextArea
            label={i18n.t('settings.locations.location.work_spaces.add.description')}
            value={workSpaceForm.values.description}
            onChange={(value) => workSpaceForm.setFieldValue('description', value)}
            mandatory
            attachmentPosition="bottom left"
          />
          <Divider />
          <Text.Normal weight="medium" color="grey1000">
            {i18n.t('settings.locations.location.work_spaces.add.image')}
          </Text.Normal>
          <FileUploader
            placeholder={i18n.t('settings.locations.location.work_areas.map.image_placeholder')}
            description=""
            selectedFile={undefined}
            onDrop={(files) => {
              setSelectedFiles((prev) => prev.concat(files))
            }}
            acceptedFormats={{ 'image/*': [] }}
          />
          {workSpace?.images
            ?.filter((image) => !imagesIdsToRemove.includes(image.id))
            .map((image) => (
              <FileItem
                aria-label={`image-${image.id}`}
                key={`existing-${image.id}`}
                file={
                  new File([], image.url ? image.url.split('/').pop() || 'image.jpg' : 'image.jpg')
                }
                actions={[
                  {
                    label: i18n.t(
                      'settings.locations.location.work_spaces.add.image_item_delete_label',
                      {
                        filename: image.url ? image.url.split('/').pop() : 'image',
                      }
                    ),
                    onClick: () => setImagesIdsToRemove((prev) => [...prev, image.id]),
                  },
                ]}
              />
            ))}

          {selectedFiles.map((file, i) => (
            <FileItem
              aria-label={`image-${file.name}`}
              key={i}
              file={file}
              actions={[
                {
                  label: i18n.t(
                    'settings.locations.location.work_spaces.add.image_item_delete_label',
                    {
                      filename: file.name,
                    }
                  ),
                  onClick: () => setSelectedFiles((prev) => prev.filter((_, _i) => _i !== i)),
                },
              ]}
            />
          ))}
          <Divider />
          <Text.Normal weight="medium" color="grey1000">
            {i18n.t('settings.locations.location.work_spaces.add.subtitle_bookings')}
          </Text.Normal>
          <Box flexDirection="row" gap="s16">
            <TimePicker
              onChange={(value) => workSpaceForm.setFieldValue('startTime', value)}
              value={workSpaceForm.values.startTime}
              label={i18n.t('settings.locations.location.work_spaces.add.start_time')}
              mandatory
              attachmentPosition="bottom right"
            />
            <TimePicker
              onChange={(value) => workSpaceForm.setFieldValue('endTime', value)}
              value={workSpaceForm.values.endTime}
              label={i18n.t('settings.locations.location.work_spaces.add.end_time')}
              mandatory
              attachmentPosition="bottom left"
              error={workSpaceForm.errors.endTime}
            />
          </Box>
          <Select
            label={i18n.t('settings.locations.location.work_spaces.add.slot_configuration')}
            options={slotConfigurationOptions}
            value={workSpaceForm.values.slotSetup}
            onChange={onChangeTimeSlot}
          />
          {isTimeSlot && (
            <TimePicker
              onChange={(value) => workSpaceForm.setFieldValue('slotDuration', value)}
              value={workSpaceForm.values.slotDuration}
              error={workSpaceForm.errors.slotDuration}
              label={i18n.t('settings.locations.location.work_spaces.add.slot_duration')}
              mandatory
            />
          )}
          {multipleBookingsSwithIsEnabled() && (
            <Card>
              <Card.Title
                action={
                  <TogglerCheckbox
                    value={workSpaceForm.values.allowMultipleBookings}
                    onChange={() =>
                      workSpaceForm.setFieldValue(
                        'allowMultipleBookings',
                        !workSpaceForm.values.allowMultipleBookings
                      )
                    }
                  />
                }
              >
                {i18n.t('settings.locations.location.work_spaces.add.concurrent_label')}
              </Card.Title>
              <Card.Description>
                {i18n.t('settings.locations.location.work_spaces.add.concurrent_description')}
              </Card.Description>
            </Card>
          )}
        </Box>
      </FormRow>
    </Sidepanel>
  )
}
export default observer(AddWorkSpace)
